import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { CAPABILITY_TYPE_OPTIONS } from '../../../constants/builderCategories';
import { colors } from '../../../designTokens';
import {
  CORE_STEP_KEYS,
  getOptionsForCapabilityType,
  OUTPUT_OPTIONS,
  ROLE_OPTIONS,
  STYLE_OPTIONS,
  SUMMARY_SETTING_KEYS,
  USAGE_SURFACE_OPTIONS,
  normalizeUsageSurfaceValue,
} from './createFlowConstants';
import { assistantPrimaryButtonSx, assistantTextButtonSx } from '../../freestyle/assistantButtonStyles';

const FIELD_LABELS = {
  capabilityType: 'Type',
  role: 'Role',
  output: 'Output type',
  style: 'Style',
  usage_surface: 'Run platform',
  rules: 'Requirements',
};

function getBriefIssues(data = {}) {
  const type = data.capabilityType || 'prompt';
  const required = [];
  const recommended = [];
  if (type !== 'prompt' && !String(data.usage_surface || '').trim()) required.push('run platform');
  if (type !== 'prompt' && !String(data.inputs || '').trim()) required.push('required inputs');
  if (type !== 'prompt' && !String(data.successCriteria || '').trim()) required.push('success criteria');
  if (type === 'agent' && !String(data.trigger || '').trim()) required.push('trigger');
  if (type === 'agent' && !String(data.approvalBoundary || '').trim()) required.push('approval boundary');
  if (!String(data.dependencies || '').trim()) recommended.push('dependencies');
  if (!String(data.rules || '').trim()) recommended.push('constraints');
  return { required, recommended };
}

const BRIEF_FIELDS = {
  inputs: { label: 'What it needs', placeholder: 'Information, files, or context it should use' },
  dependencies: { label: 'Tools & access', placeholder: 'Tools, integrations, or setup it relies on' },
  successCriteria: { label: 'How you will know it worked', placeholder: 'What should be true when it is complete?' },
  trigger: { label: 'What starts it', placeholder: 'An event, schedule, or user action' },
  approvalBoundary: { label: 'When it should check with you', placeholder: 'What it can do on its own and what needs approval' },
};

function getFieldLabel(fieldKey, capabilityType) {
  if (fieldKey === 'role' && capabilityType !== 'prompt') return 'Operating role';
  if (fieldKey === 'output' && capabilityType !== 'prompt') return 'Completion';
  if (fieldKey === 'style' && capabilityType !== 'prompt') return 'Operating style';
  return FIELD_LABELS[fieldKey] || fieldKey;
}

const CREATE_FLOW_FIELD_LABEL_SX = {
  display: 'block',
  mb: 0.5,
  color: colors.textPrimary,
  fontSize: '0.75rem',
  fontWeight: 700,
  lineHeight: 1.35,
};

const CREATE_FLOW_INPUT_SX = {
  '& .MuiInputBase-root': {
    borderRadius: '8px',
    bgcolor: 'background.paper',
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-input': {
    color: colors.textPrimary,
    fontSize: '0.875rem',
    lineHeight: 1.55,
  },
};

const FIELD_OPTIONS = {
  role: ROLE_OPTIONS,
  output: OUTPUT_OPTIONS,
  style: STYLE_OPTIONS,
  usage_surface: USAGE_SURFACE_OPTIONS,
  capabilityType: CAPABILITY_TYPE_OPTIONS.map((t) => t.value),
};

function getFieldOptions(fieldKey, capabilityType) {
  if (fieldKey === 'capabilityType') return FIELD_OPTIONS.capabilityType;
  if (['role', 'output', 'style', 'usage_surface'].includes(fieldKey)) {
    return getOptionsForCapabilityType(capabilityType, fieldKey);
  }
  return FIELD_OPTIONS[fieldKey] || [];
}

function getDisplayValue(fieldKey, value) {
  if (fieldKey === 'capabilityType') {
    const match = CAPABILITY_TYPE_OPTIONS.find((t) => t.value === value);
    return match?.label || value;
  }
  if (fieldKey === 'usage_surface') {
    return normalizeUsageSurfaceValue(value);
  }
  return value;
}

function TypeDifferentiatorHelp() {
  return (
    <Tooltip
      arrow
      placement="top"
      title={(
        <Box sx={{ maxWidth: 340, py: 0.25 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: 'inherit', mb: 0.5 }}>
            Capability types
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'inherit', lineHeight: 1.45 }}>
            Simple: chat prompts that can run without integrations or agent runtimes.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'inherit', lineHeight: 1.45, mt: 0.45 }}>
            Integrated: prompt workflows that use tools, platforms, files, or integrations.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'inherit', lineHeight: 1.45, mt: 0.45 }}>
            Autonomous: scheduled or event-driven workflows with reduced human involvement.
          </Typography>
        </Box>
      )}
    >
      <IconButton
        size="small"
        aria-label="Simple, integrated, and autonomous differences"
        sx={{
          width: 26,
          height: 26,
          color: 'text.secondary',
          '&:hover': { color: 'primary.main', bgcolor: 'rgba(29, 78, 216, 0.06)' },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 17 }} />
      </IconButton>
    </Tooltip>
  );
}

function InferenceFieldToken({
  fieldKey,
  label,
  value,
  options = [],
  confidence,
  onChange,
}) {
  const [anchor, setAnchor] = useState(null);
  const [customValue, setCustomValue] = useState('');
  const hasValue = Boolean(String(value || '').trim());
  const isInferred = hasValue && confidence !== 'low';
  const isTypeField = fieldKey === 'capabilityType';
  const display = hasValue ? getDisplayValue(fieldKey, value) : 'Not set — click to choose';

  const handleOpen = (event) => {
    setCustomValue(hasValue && !isTypeField ? String(value) : '');
    setAnchor(event.currentTarget);
  };

  const applyCustom = () => {
    const trimmed = fieldKey === 'usage_surface'
      ? normalizeUsageSurfaceValue(customValue)
      : customValue.trim();
    if (!trimmed) return;
    onChange?.(fieldKey, isTypeField ? trimmed.toLowerCase() : trimmed);
    setAnchor(null);
  };

  return (
    <>
      <Chip
        size="small"
        variant="outlined"
        color={!hasValue || confidence === 'low' ? 'warning' : 'default'}
        label={(
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
            <span>{`${label}: ${display}`}</span>
            <KeyboardArrowDownIcon sx={{ fontSize: 16, opacity: 0.7 }} />
          </Box>
        )}
        onClick={handleOpen}
        sx={{
          cursor: 'pointer',
          maxWidth: '100%',
          height: 'auto',
          minHeight: 32,
          color: hasValue ? colors.textPrimary : '#92400e',
          bgcolor: hasValue ? 'background.paper' : 'rgba(245, 158, 11, 0.08)',
          borderColor: hasValue ? 'divider' : 'rgba(245, 158, 11, 0.34)',
          fontSize: '0.78rem',
          fontWeight: 500,
          borderRadius: '8px',
          boxShadow: 'none',
          '& .MuiChip-label': { whiteSpace: 'normal', py: 0.6, px: 1.1, overflowWrap: 'anywhere' },
          '&:hover': {
            bgcolor: hasValue ? 'rgba(29, 78, 216, 0.12)' : 'rgba(245, 158, 11, 0.18)',
            borderColor: 'rgba(29, 78, 216, 0.36)',
          },
          ...(!isInferred && hasValue ? { borderStyle: 'dashed' } : {}),
        }}
      />
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { maxHeight: 360, maxWidth: 360 } }}
      >
        {!isTypeField && (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              top: 0,
              bgcolor: 'background.paper',
              zIndex: 1,
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Type your own…"
              value={customValue}
              onChange={(event) => setCustomValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applyCustom();
                }
              }}
            />
          </Box>
        )}
        {options.map((option) => {
          const optionValue = isTypeField ? option : option;
          const optionLabel = isTypeField ? getDisplayValue('capabilityType', option) : option;
          const selected = isTypeField ? option === value : option === value;
          return (
            <MenuItem
              key={optionValue}
              selected={selected}
              onClick={() => {
                onChange?.(fieldKey, optionValue);
                setAnchor(null);
              }}
            >
              {optionLabel}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default function CreateFlowSummaryCard({
  buildGoal = '',
  createFlowData = {},
  confidence = {},
  onConfirmGenerate,
  onFieldChange,
  generating = false,
  generated = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const goal = buildGoal || createFlowData.goal || '';
  const settingKeys = ['capabilityType', ...CORE_STEP_KEYS];
  const primarySettingKeys = ['capabilityType', 'role', ...(['skill', 'agent'].includes(createFlowData.capabilityType) ? ['usage_surface'] : [])]
    .filter((key) => settingKeys.includes(key));
  const advancedSettingKeys = settingKeys.filter((key) => !primarySettingKeys.includes(key));
  const briefIssues = getBriefIssues(createFlowData);
  const briefFields = createFlowData.capabilityType === 'agent'
    ? ['inputs', 'dependencies', 'successCriteria', 'trigger', 'approvalBoundary']
    : createFlowData.capabilityType === 'skill'
      ? ['inputs', 'dependencies', 'successCriteria']
      : ['inputs', 'successCriteria'];
  const canGenerate = goal.trim()
    && SUMMARY_SETTING_KEYS.every((key) => String(createFlowData[key] || '').trim())
    && briefIssues.required.length === 0;

  return (
    <Stack spacing={1.1} sx={{ mt: 1.25 }}>
      <PaperLike>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 0.75,
            color: colors.textPrimary,
            fontSize: '0.9375rem',
            fontWeight: 700,
            letterSpacing: 0,
          }}
        >
          Build plan
        </Typography>
        <Box>
          <Typography component="label" htmlFor="create-flow-summary" sx={CREATE_FLOW_FIELD_LABEL_SX}>
            Summary
          </Typography>
          <TextField
            id="create-flow-summary"
            size="small"
            fullWidth
            multiline
            minRows={1}
            value={goal}
            onChange={(event) => onFieldChange?.('goal', event.target.value)}
            placeholder="Describe the capability you want to build"
            sx={{
              ...CREATE_FLOW_INPUT_SX,
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ display: 'block', mt: 1.1, mb: 0.7, color: colors.textSecondary, lineHeight: 1.4 }}>
          Review your plan
        </Typography>
        <Stack spacing={1.35} sx={{ mt: 1.25 }}>
          {briefFields.map((fieldKey) => (
            <Box key={fieldKey}>
              <Typography component="label" htmlFor={`create-flow-${fieldKey}`} sx={CREATE_FLOW_FIELD_LABEL_SX}>
                {BRIEF_FIELDS[fieldKey].label}
              </Typography>
              <TextField
                id={`create-flow-${fieldKey}`}
                size="small"
                fullWidth
                multiline
                minRows={1}
                placeholder={BRIEF_FIELDS[fieldKey].placeholder}
                value={createFlowData[fieldKey] || ''}
                onChange={(event) => onFieldChange?.(fieldKey, event.target.value)}
                sx={CREATE_FLOW_INPUT_SX}
              />
            </Box>
          ))}
        </Stack>
      </PaperLike>

      <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.65, alignItems: 'center' }}>
          <TypeDifferentiatorHelp />
          {primarySettingKeys.map((key) => (
            <InferenceFieldToken
              key={key}
              fieldKey={key}
              label={getFieldLabel(key, createFlowData.capabilityType)}
              value={createFlowData[key]}
              options={getFieldOptions(key, createFlowData.capabilityType)}
              confidence={confidence[key]}
              onChange={onFieldChange}
            />
          ))}
          <Button
            size="small"
            endIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />}
            onClick={() => setExpanded((prev) => !prev)}
            sx={{ ...assistantTextButtonSx, ml: 0.25 }}
          >
            {expanded ? 'Hide advanced' : 'Advanced settings'}
          </Button>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Stack spacing={0.85} sx={{ pt: 0.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.65, alignItems: 'center' }}>
            {advancedSettingKeys.map((key) => (
              <InferenceFieldToken
                key={key}
                fieldKey={key}
                label={getFieldLabel(key, createFlowData.capabilityType)}
                value={createFlowData[key]}
                options={getFieldOptions(key, createFlowData.capabilityType)}
                confidence={confidence[key]}
                onChange={onFieldChange}
              />
            ))}
          </Box>
          <Box>
            <Typography component="label" htmlFor="create-flow-requirements" sx={CREATE_FLOW_FIELD_LABEL_SX}>
              Requirements
            </Typography>
            <TextField
              id="create-flow-requirements"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={createFlowData.rules || ''}
              onChange={(event) => onFieldChange?.('rules', event.target.value)}
              placeholder="Constraints, must-haves, or things to avoid (optional)"
              sx={CREATE_FLOW_INPUT_SX}
            />
          </Box>
        </Stack>
      </Collapse>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, pt: 0.5 }}>
      <Box sx={{ flex: '1 1 220px' }}>
        {generated ? (
          <Typography variant="caption" color="text.secondary">Draft generated</Typography>
        ) : briefIssues.required.length > 0 ? (
          <Typography variant="caption" sx={{ color: '#92400E', fontWeight: 700, lineHeight: 1.45 }}>
            Needs details before generation: {briefIssues.required.join(', ')}.
          </Typography>
        ) : briefIssues.recommended.length > 0 ? (
          <Typography variant="caption" sx={{ color: colors.textSecondary, lineHeight: 1.45 }}>
            Optional refinements: {briefIssues.recommended.join(', ')}.
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ color: '#166534', fontWeight: 700 }}>
            Ready to generate.
          </Typography>
        )}
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          variant="contained"
          disabled={generated || generating || !canGenerate}
          onClick={onConfirmGenerate}
          sx={assistantPrimaryButtonSx}
        >
          {generated ? 'Draft generated' : generating ? 'Generating…' : 'Generate capability'}
        </Button>
      </Stack>
      </Box>
    </Stack>
  );
}

function PaperLike({ children }) {
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'rgba(15, 23, 42, 0.08)',
        bgcolor: 'background.paper',
      }}
    >
      {children}
    </Box>
  );
}
